import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import { TimerProvider, useTimer } from './context/TimerContext';
import { ChessTimerProvider } from './context/ChessTimerContext';
import { TimerDisplay } from './components/Timer/TimerDisplay';
import { ChessTimer } from './components/Chess/ChessTimer';
import { SettingsPanel } from './components/Settings/SettingsPanel';
import { Settings as SettingsIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { PaywallModal } from './components/Auth/PaywallModal';
import { PremiumModal } from './components/Premium/PremiumModal';
import { AuthCallback } from './pages/auth/callback';
import { CheckoutSuccess } from './pages/checkout/success';
import { CheckoutCancel } from './pages/checkout/cancel';
import { Toast } from './components/Notifications/Toast';
import { PwaUpdateToast } from './components/PwaUpdateToast';
import { setupUrlActionHandler } from './utils/urlActions';

function TimerApp() {
  const [showSettings, setShowSettings] = React.useState(false);
  const [showPremiumModal, setShowPremiumModal] = React.useState(false);
  const [showModeMenu, setShowModeMenu] = React.useState(false);
  const [currentMode, setCurrentMode] = React.useState<'pomodoro' | 'chess'>('pomodoro');
  const timer = useTimer();

  useEffect(() => {
    setupUrlActionHandler(timer);
  }, [timer]);

  // Handle online/offline status
  useEffect(() => {
    function handleOnline() {
      // Sync with service worker when coming back online
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SYNC_TIMER'
        });
      }
    }

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Effect for keyboard shortcuts (Settings Toggle & Escape to Close)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+, or Cmd+, to toggle settings
      if (event.key === ',' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setShowSettings(prevShowSettings => !prevShowSettings);
      }
      // Check for Escape key to close settings, *only if settings are open*
      else if (event.key === 'Escape' && showSettings) {
        event.preventDefault(); // Prevent other potential Escape key actions
        setShowSettings(false); // Close the settings panel
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // Add showSettings to dependency array so the handler always knows the current state
  }, [showSettings, setShowSettings]);

  return (
    <div className="app-shell fixed inset-0 bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <PwaUpdateToast />
      
      {/* Mode selector dropdown (top left) */}
      <div className="fixed top-4 left-4 z-50">
        <div className="relative">
          <button
            onClick={() => setShowModeMenu(!showModeMenu)}
            className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-all text-sm font-medium text-gray-700 dark:text-gray-300"
            aria-label="Select timer mode"
          >
            <span>{currentMode === 'pomodoro' ? 'Pomodoro' : 'Chess'}</span>
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showModeMenu && (
            <>
              <div 
                className="fixed inset-0 z-[-1]" 
                onClick={() => setShowModeMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-[1] overflow-hidden">
                <button
                  onClick={() => {
                    setCurrentMode('pomodoro');
                    setShowModeMenu(false);
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    currentMode === 'pomodoro'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Pomodoro Timer
                </button>
                <button
                  onClick={() => {
                    setCurrentMode('chess');
                    setShowModeMenu(false);
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    currentMode === 'chess'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Chess Timer
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <button
        onClick={() => setShowSettings(true)}
        className="fixed top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Toggle settings"
      >
        <SettingsIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
      </button>
      
      <div className="timer-shell w-full max-w-md">
        {currentMode === 'pomodoro' ? (
          <TimerDisplay isSettingsOpen={showSettings} />
        ) : (
          <ChessTimer />
        )}
      </div>

      {showSettings && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
          onShowPremium={() => setShowPremiumModal(true)}
        />
      )}
      <PaywallModal />
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />
      <Toast />
    </div>
  );
}

function App() {
  return (
    <Router>
      <TimerProvider>
        <ChessTimerProvider>
          <Routes>
            <Route path="/" element={<TimerApp />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          </Routes>
        </ChessTimerProvider>
      </TimerProvider>
    </Router>
  );
}

export default App;
